import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../api/axiosConfig';

/**
 * Employee master maintenance.
 *
 * The same collection the payslip module maintains — one staff list, two
 * audiences. HR keeps names, branches, designations and QIDs current here; the
 * new-request form's picker reads it back to pre-fill employee details.
 *
 * This tab only renders for coordinators, but that is convenience: every write
 * is re-checked server-side in clearanceEmployeeController.
 */

const EMPTY = {
    employeeCode: '', fullName: '', mobile: '', email: '',
    company: '', department: '', branch: '', designation: '',
    qid: '', qidExpiry: '', isActive: true
};

const IMPORT_MODES = [
    {
        value: 'merge',
        label: 'Update existing + add new',
        hint: 'Matches on staff code. Overwrites only the columns your sheet contains, and adds codes not yet on file.'
    },
    {
        value: 'add-new',
        label: 'Add new only',
        hint: 'Adds staff codes not yet on file. Employees already in the master are left completely untouched.'
    }
];

const EmployeeMasterTab = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [includeInactive, setIncludeInactive] = useState(false);
    const [editing, setEditing] = useState(null);      // null | EMPTY-shaped draft
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const [showImport, setShowImport] = useState(false);
    const [importMode, setImportMode] = useState('merge');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileRef = useRef(null);

    const fetchEmployees = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ manage: 'true' });
            if (search.trim()) params.append('search', search.trim());
            if (includeInactive) params.append('includeInactive', 'true');
            const res = await axiosInstance.get(`/api/clearance/employees?${params.toString()}`);
            if (res.data?.success) setEmployees(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load the employee master');
        } finally {
            setLoading(false);
        }
    }, [search, includeInactive]);

    // Debounced so typing in the search box does not fire a request per keystroke.
    useEffect(() => {
        const t = setTimeout(fetchEmployees, 300);
        return () => clearTimeout(t);
    }, [fetchEmployees]);

    useEffect(() => {
        if (!notice) return undefined;
        const t = setTimeout(() => setNotice(''), 4000);
        return () => clearTimeout(t);
    }, [notice]);

    const save = async () => {
        if (!editing) return;
        setError('');

        if (!editing.employeeCode?.trim() || !editing.fullName?.trim()) {
            setError('Staff code and name are required');
            return;
        }

        const payload = {
            fullName: editing.fullName.trim(),
            email: editing.email?.trim() || null,
            company: editing.company?.trim() || null,
            department: editing.department?.trim() || null,
            branch: editing.branch?.trim() || null,
            designation: editing.designation?.trim() || null,
            qid: editing.qid?.trim() || null,
            qidExpiry: editing.qidExpiry || null,
            isActive: editing.isActive
        };

        // The list only ever carries a masked number, so the edit field starts
        // blank. Omitting the key leaves the stored number alone; sending "" would
        // read as an attempt to clear it.
        const mobile = editing.mobile?.trim();
        if (mobile) payload.mobile = mobile;

        try {
            if (editing._id) {
                await axiosInstance.put(`/api/clearance/employees/${editing._id}`, payload);
                setNotice(`${editing.employeeCode} updated`);
            } else {
                await axiosInstance.post('/api/clearance/employees', {
                    ...payload,
                    employeeCode: editing.employeeCode.trim()
                });
                setNotice(`${editing.employeeCode.trim().toUpperCase()} added`);
            }
            setEditing(null);
            fetchEmployees();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save the employee');
        }
    };

    const remove = async (employee) => {
        const ok = window.confirm(
            `Remove ${employee.employeeCode} — ${employee.fullName} from the master?\n\n` +
            'This also removes them from payslip distribution. Clearance forms already ' +
            'raised for them keep their own copy of the details.'
        );
        if (!ok) return;

        setError('');
        try {
            const res = await axiosInstance.delete(`/api/clearance/employees/${employee._id}`);
            setNotice(res.data?.message || 'Employee removed');
            fetchEmployees();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to remove the employee');
        }
    };

    const runImport = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) {
            setError('Choose an Excel or CSV file first');
            return;
        }

        setImporting(true);
        setError('');
        setImportResult(null);
        try {
            const body = new FormData();
            body.append('file', file);
            body.append('mode', importMode);
            // A few hundred rows are validated and written one at a time, which can
            // outrun the client's default 30s. Content-Type is left to axios so the
            // multipart boundary is generated correctly.
            const res = await axiosInstance.post('/api/clearance/employees/import', body, { timeout: 120000 });
            if (res.data?.success) {
                setImportResult(res.data.data);
                setNotice(res.data.message);
                if (fileRef.current) fileRef.current.value = '';
                fetchEmployees();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    const downloadTemplate = async () => {
        try {
            const res = await axiosInstance.get('/api/clearance/employees/template', { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'employee-master-template.xlsx';
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            setError('Failed to download the template');
        }
    };

    return (
        <div className="employee-master">
            {error && <div className="clearance-error">{error}</div>}
            {notice && <div className="clearance-notice">{notice}</div>}

            <div className="clearance-toolbar">
                <div className="toolbar-left">
                    <input
                        type="text"
                        className="clearance-search"
                        placeholder="Search code, name, branch, designation or QID…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <label className="em-check">
                        <input
                            type="checkbox"
                            checked={includeInactive}
                            onChange={(e) => setIncludeInactive(e.target.checked)}
                        />
                        Show inactive
                    </label>
                </div>
                <div className="em-actions">
                    <button
                        className="btn btn-secondary"
                        onClick={() => { setShowImport(s => !s); setImportResult(null); }}
                    >
                        {showImport ? 'Close import' : 'Import from Excel'}
                    </button>
                    <button className="btn btn-primary" onClick={() => { setEditing({ ...EMPTY }); setError(''); }}>
                        + Add Employee
                    </button>
                </div>
            </div>

            {showImport && (
                <div className="em-import card">
                    <div className="em-import-head">
                        <h3>Import employees</h3>
                        <button className="btn-link" onClick={downloadTemplate}>Download template</button>
                    </div>
                    <p className="form-hint">
                        Columns are matched by header name, so your existing staff list works as-is:
                        <strong> Code, Employee Name, Branch, Designation, QID</strong> (Mobile, Email,
                        Company and Department are also read if present). A column your sheet does not
                        have is never blanked out — importing the HR list will not wipe payroll numbers.
                    </p>

                    <div className="em-modes">
                        {IMPORT_MODES.map(m => (
                            <label key={m.value} className={`em-mode ${importMode === m.value ? 'is-selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="import-mode"
                                    value={m.value}
                                    checked={importMode === m.value}
                                    onChange={() => setImportMode(m.value)}
                                />
                                <span className="em-mode-label">{m.label}</span>
                                <span className="em-mode-hint">{m.hint}</span>
                            </label>
                        ))}
                    </div>

                    <div className="em-import-run">
                        <input type="file" ref={fileRef} accept=".xlsx,.xls,.csv" />
                        <button className="btn btn-primary" onClick={runImport} disabled={importing}>
                            {importing ? 'Importing…' : 'Run import'}
                        </button>
                    </div>

                    {importResult && (
                        <div className="em-import-result">
                            <div className="em-result-counts">
                                <span className="em-count em-count-ok">{importResult.created} added</span>
                                <span className="em-count">{importResult.updated} updated</span>
                                {importResult.skippedCount > 0 && (
                                    <span className="em-count">{importResult.skippedCount} skipped</span>
                                )}
                                {importResult.failedCount > 0 && (
                                    <span className="em-count em-count-bad">{importResult.failedCount} failed</span>
                                )}
                                <span className="em-count em-count-muted">{importResult.totalRows} rows read</span>
                            </div>

                            {importResult.skipped?.length > 0 && (
                                <details className="em-rows">
                                    <summary>Skipped — already in the master ({importResult.skippedCount})</summary>
                                    <ul>
                                        {importResult.skipped.map(r => (
                                            <li key={`s-${r.row}`}>Row {r.row} · {r.employeeCode} — {r.reason}</li>
                                        ))}
                                    </ul>
                                </details>
                            )}

                            {importResult.failed?.length > 0 && (
                                <details className="em-rows em-rows-bad" open>
                                    <summary>Not imported ({importResult.failedCount})</summary>
                                    <ul>
                                        {importResult.failed.map(r => (
                                            <li key={`f-${r.row}`}>
                                                Row {r.row}{r.employeeCode ? ` · ${r.employeeCode}` : ''} — {r.reason}
                                            </li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}
                </div>
            )}

            {editing && (
                <div className="em-editor card">
                    <h3>{editing._id ? `Edit ${editing.employeeCode}` : 'Add employee'}</h3>
                    <div className="form-grid">
                        <label>
                            <span>Staff Code *</span>
                            <input
                                value={editing.employeeCode}
                                disabled={Boolean(editing._id)}
                                onChange={(e) => setEditing({ ...editing, employeeCode: e.target.value })}
                                placeholder="B1004"
                            />
                        </label>
                        <label>
                            <span>Full Name *</span>
                            <input
                                value={editing.fullName}
                                onChange={(e) => setEditing({ ...editing, fullName: e.target.value })}
                            />
                        </label>
                        <label>
                            <span>Branch</span>
                            <input
                                value={editing.branch || ''}
                                onChange={(e) => setEditing({ ...editing, branch: e.target.value })}
                                placeholder="Wholesale"
                            />
                        </label>
                        <label>
                            <span>Designation</span>
                            <input
                                value={editing.designation || ''}
                                onChange={(e) => setEditing({ ...editing, designation: e.target.value })}
                                placeholder="Sales Assistant"
                            />
                        </label>
                        <label>
                            <span>QID</span>
                            <input
                                value={editing.qid || ''}
                                onChange={(e) => setEditing({ ...editing, qid: e.target.value })}
                                placeholder="29182603456"
                            />
                        </label>
                        <label>
                            <span>QID Expiry</span>
                            <input
                                type="date"
                                value={editing.qidExpiry ? String(editing.qidExpiry).slice(0, 10) : ''}
                                onChange={(e) => setEditing({ ...editing, qidExpiry: e.target.value })}
                            />
                        </label>
                        <label>
                            <span>Mobile {editing._id && <em className="em-muted">(blank keeps current)</em>}</span>
                            <input
                                value={editing.mobile || ''}
                                onChange={(e) => setEditing({ ...editing, mobile: e.target.value })}
                                placeholder="+974 5514 9207"
                            />
                        </label>
                        <label>
                            <span>Department</span>
                            <input
                                value={editing.department || ''}
                                onChange={(e) => setEditing({ ...editing, department: e.target.value })}
                            />
                        </label>
                    </div>

                    {editing._id && (
                        <label className="em-check em-check-block">
                            <input
                                type="checkbox"
                                checked={editing.isActive}
                                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                            />
                            Active — inactive staff are hidden from the clearance picker and never sent payslips
                        </label>
                    )}

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={() => { setEditing(null); setError(''); }}>
                            Cancel
                        </button>
                        <button className="btn btn-primary" onClick={save}>Save</button>
                    </div>
                </div>
            )}

            <div className="clearance-list card">
                {loading ? (
                    <div className="clearance-empty">Loading employees…</div>
                ) : employees.length === 0 ? (
                    <div className="clearance-empty">
                        {search ? 'No employee matches that search.' : 'The employee master is empty — import your staff list to get started.'}
                    </div>
                ) : (
                    <>
                        <table className="clearance-table">
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Name</th>
                                    <th>Branch</th>
                                    <th>Designation</th>
                                    <th>QID</th>
                                    <th>Mobile</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.map(emp => (
                                    <tr key={emp._id} className={emp.isActive ? '' : 'em-row-inactive'}>
                                        <td>{emp.employeeCode}</td>
                                        <td>
                                            {emp.fullName}
                                            {!emp.isActive && <span className="em-tag">Inactive</span>}
                                        </td>
                                        <td>{emp.branch || '—'}</td>
                                        <td>{emp.designation || '—'}</td>
                                        <td>{emp.qid || '—'}</td>
                                        <td className={emp.hasMobile ? '' : 'em-muted'}>
                                            {emp.hasMobile ? emp.mobile : 'not set'}
                                        </td>
                                        <td className="em-row-actions">
                                            <button
                                                className="btn-open"
                                                onClick={() => { setEditing({ ...emp, mobile: '' }); setError(''); }}
                                            >
                                                Edit
                                            </button>
                                            <button className="btn-danger-link" onClick={() => remove(emp)}>
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="em-footnote">{employees.length} employee(s)</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default EmployeeMasterTab;
